import { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { ApiError } from 'services/ApiService'
import theme from 'stories/utils/theme'
import { ExpansionBannerSlice } from 'interfaces/GeneratedLandingPage'
import { Body1 } from 'stories/elements/Typography/Text'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseLocale } from 'lib/constants/localization'
import { useRouter } from 'next/router'
import KnowService from 'services/KnowService'
import usePrevious from 'lib/hooks/usePrevious'

interface BannerProps {
    backgroundColor: string
    hideOnScrollDownMobile?: boolean
}

const Container = styled.div``

const Placeholder = styled.div<BannerProps>`
    height: 50px;
    width: 100vw;
    max-width: 100%;
    background: ${({ backgroundColor }) => backgroundColor ?? theme.colors.deepSeaBlue};

    @media (max-width: 750px) {
        height: 80px;
    }
`

const BannerWrapperDesktop = styled.div<BannerProps>`
    position: fixed;
    background: ${({ backgroundColor }) => backgroundColor ?? theme.colors.deepSeaBlue};
    top: var(--navbar-height);
    width: 100vw;
    height: 50px;
    display: flex;
    justify-content: space-between;
    z-index: ${theme.zIndex.topBanner};
    padding: 0 50px;

    span {
        margin-top: -5px;
        color: ${theme.colors.white};
        line-height: 60px;
        text-align: center;
    }
    p {
        white-space: nowrap;
        z-index: 10;
        color: ${theme.colors.white};
        line-height: 60px;
        text-align: center;
    }

    @media (max-width: 750px) {
        display: none;
    }
`

const DarkerBorder = styled.span`
    background-color: var(--secondary-color-darker);
    min-width: 15px;
    border-radius: 10px;
    margin: 0 2px;
    padding: 5px 10px;
`

const BannerWrapperMobile = styled.div<BannerProps>`
    position: fixed;
    min-height: 60px;
    grid-template-columns: 1fr;
    background: ${({ backgroundColor }) => backgroundColor ?? theme.colors.deepSeaBlue};
    top: 60px;
    width: 100vw !important;
    height: auto;
    z-index: ${theme.zIndex.topBanner};
    padding-top: 0px;

    & p {
        text-align: center;
        display: block;
        color: white;
        height: auto;
        padding-top: 3px;
    }

    transition: transform 0.2s;
    ${({ hideOnScrollDownMobile }) =>
        hideOnScrollDownMobile
            ? css`
                  transform: translateY(calc(-1 * 60px));
              `
            : ''}

    @media (min-width: 751px) {
        display: none;
    }
`
const DiscountText = styled.div`
    display: flex;
    z-index: ${theme.zIndex.topBanner};
    p {
        margin-top: -5px;
    }
    @media (max-width: 750px) {
        text-align: center;
    }
`

const MainText = styled(Body1)`
    display: flex;
    justify-content: flex-end;
    height: 50px;
    margin: 0;

    span {
        font-size: inherit;
        font-weight: 500;
    }
    .space {
        margin-left: 15px;
    }
`

const MobileTopBannerWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    place-items: center;
    z-index: ${theme.zIndex.topBanner};
    padding: 5px 25px;
`

interface Props {
    data: ExpansionBannerSlice['primary']
}
const ExpansionBanner = ({ data }: Props) => {
    const [knowsUploaded, setKnowsUploaded] = useState<number | null>()
    const { t: tCommon } = _useTranslation('common')
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const [scrollPosition, setScrollPosition] = useState(0)
    const prevScrollPosition = usePrevious(scrollPosition)

    useEffect(() => {
        window.addEventListener('scroll', listenToScroll)
        return () => {
            window.removeEventListener('scroll', listenToScroll)
        }
    }, [])

    const listenToScroll = () => {
        setScrollPosition(document.body.scrollTop || document.documentElement.scrollTop)
    }

    useEffect(() => {
        KnowService.countByCountry(parsedLocale.countryCode, parsedLocale.contentLanguageCode)
            .then((count) => {
                setKnowsUploaded(count)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'know.countByCountry')
                } else {
                    throw error
                }
            })
    }, [parsedLocale.contentLanguageCode, parsedLocale.countryCode, tCommon])

    const hideOnMobile = useMemo(
        () => !!(prevScrollPosition && scrollPosition > prevScrollPosition),
        [prevScrollPosition, scrollPosition]
    )

    const mobileChildren = (
        <MobileTopBannerWrapper>
            <div>
                <DiscountText>
                    <Body1 color={theme.colors.white}>{data.text}</Body1>
                </DiscountText>
                <MainText>
                    <DarkerBorder>{knowsUploaded ?? '-'}</DarkerBorder> {data.amount_label}
                </MainText>
            </div>
        </MobileTopBannerWrapper>
    )

    return (
        <Container>
            <Placeholder backgroundColor={data.placeholder_background_color} hideOnScrollDownMobile={hideOnMobile} />
            <BannerWrapperDesktop backgroundColor={data.background_color}>
                <DiscountText>
                    {data.svg?.url ? (
                        <img
                            src={data.svg.url ?? ''}
                            height={50}
                            width={data.svg?.dimensions?.height}
                            alt={data.svg?.alt ?? 'expansion banner logo'}
                        />
                    ) : null}
                    <Body1 color={theme.colors.white}>{data.text}</Body1>
                </DiscountText>
                <MainText>
                    <span>
                        <DarkerBorder>{knowsUploaded ?? '-'}</DarkerBorder>
                        <span className="space">{data.amount_label}</span>
                    </span>
                </MainText>
            </BannerWrapperDesktop>

            <BannerWrapperMobile backgroundColor={data.background_color} hideOnScrollDownMobile={hideOnMobile}>
                {mobileChildren}
            </BannerWrapperMobile>
        </Container>
    )
}

export default ExpansionBanner
